import React, { useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import '../css/creator/MediaGridDropdown.css';
import { ROLE, CONTENT_PRICING } from '../../../shared/constants/common';
import { confirmDangerousAction, showError } from '../../../shared/utils/swalHandler';
import {
  deleteContent as deleteContentAction,
  updateContent as updateContentAction
} from '../../../shared/actions/contentAction';
import UserTag from '../../../views/Modals/UserTag';
import { useModalOpen } from '../../../shared/utils/modalUtils.js'; 

const MENU_CONTAINER_SELECTOR = '.creator-feed-menu-container';

const MediaGridDropdown = ({
  className = '',
  iconClass = '',
  post,
  userRole = null,
  isShowPin = false,
  isPinAlreadyExists = false,
  isOpen = false,
  onToggle,
  onDeleted,
  onUpdated,
}) => {
  const dispatch = useDispatch();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const [priceError, setPriceError] = useState('');
  const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);
  const [isLockingWithPrice, setIsLockingWithPrice] = useState(false);

  // Edit caption modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editCaption, setEditCaption] = useState('');
  const [isUpdatingEdit, setIsUpdatingEdit] = useState(false);
  const [selectedTaggedUsers, setSelectedTaggedUsers] = useState([]);
  const [isTagPopupOpen, setIsTagPopupOpen] = useState(false);

  // Add/remove class on HTML tag when modal opens/closes
  useModalOpen(isEditModalOpen || isPriceModalOpen || isTagPopupOpen);

  // Get unlock/lock text based on tab type
  const getUnlockText = useCallback(() => {
    return post.lock_content ? 'Unlock Content' : 'Lock Content';
  }, [post.lock_content]);

  // Get unlock/lock icon based on tab type
  const getUnlockIcon = useCallback(() => {
    return post.lock_content ? 'fa-unlock' : 'fa-lock';
  }, [post.lock_content]);

  // Check if user has permission to change prices (Role 3 - CREATOR_CONTENT only and not on Free tab)
  // && post.type_content !== TAB_TYPE.FREE
  const canChangePrice = useCallback(() => {
    return `${userRole}` === `${ROLE.CREATOR_CONTENT}` && post.lock_content;
  }, [userRole, post.lock_content]);

  // Toggle menu
  const toggleMenu = useCallback((e) => {
    e.stopPropagation();
    onToggle?.(post._id || post.id);
  }, [onToggle, post._id, post.id]);

  // Close menu
  const closeMenu = useCallback(() => {
    onToggle?.(null);
  }, [onToggle]);

  // Unlock content
  const onUnlock = useCallback((post) => {
    const contentId = post?._id || post?.id;
    if (!contentId) return;
    dispatch(updateContentAction(contentId, { lock: false }, (res) => {
      if (res?.success) {
        onUpdated?.('unlock', contentId);
      }
    }));
  }, [dispatch, onUpdated]);

  // Lock content (with role-based behavior)
  const onLock = useCallback((post) => {
    const contentId = post?._id || post?.id;
    if (!contentId) return;

    // If the user is a creator, prompt for price first and then lock with price
    if (`${userRole}` === `${ROLE.CREATOR_CONTENT}`) {
      const initial = post?.real_price || post?.price || '';
      setPriceInput(initial?.toString());
      setPriceError('');
      setIsLockingWithPrice(true);
      setIsPriceModalOpen(true);
      return;
    }

    // Non-creator: lock immediately without price
    dispatch(updateContentAction(contentId, { lock: true }, (res) => {
      if (res?.success) {
        onUpdated?.('lock', contentId);
      }
    }));
  }, [dispatch, onUpdated, userRole]);

  // Open edit modal
  const onEdit = useCallback((post) => {
    setEditCaption(post?.caption || '');
    const initialTags = Array.isArray(post?.tag_users)
      ? post.tag_users.map((u) => {
        if (typeof u === 'string') return { username: u };
        return u;
      })
      : [];
    setSelectedTaggedUsers(initialTags);
    setIsEditModalOpen(true);
  }, []);

  // Open price change modal
  const onPriceChange = useCallback((post) => {
    const initial = post?.real_price || post?.price || '';
    setPriceInput(initial?.toString());
    setPriceError('');
    setIsPriceModalOpen(true);
  }, []);

  // Modal handlers
  const closePriceModal = useCallback(() => {
    if (isUpdatingPrice) return;
    setIsPriceModalOpen(false);
    setPriceError('');
  }, [isUpdatingPrice]);

  const closeEditModal = useCallback(() => {
    if (isUpdatingEdit) return;
    setIsEditModalOpen(false);
  }, [isUpdatingEdit]);

  const openTagPopup = useCallback(() => setIsTagPopupOpen(true), []);
  const closeTagPopup = useCallback(() => setIsTagPopupOpen(false), []);

  // Tag management
  const handleTagConfirm = useCallback((users) => {
    setSelectedTaggedUsers(users || []);
    setIsTagPopupOpen(false);
  }, []);

  const handleRemoveTaggedUser = useCallback((username) => {
    setSelectedTaggedUsers((prev) => (Array.isArray(prev) ? prev.filter((u) => u && u.username !== username) : []));
  }, []);

  // Save caption and tags
  const handleEditSave = useCallback(() => {
    const contentId = post?._id || post?.id;
    if (!contentId) return;

    const tagIds = (selectedTaggedUsers || [])
      .map((u) => u?._id || u?.id)
      .filter(Boolean);

    const payload = { caption: editCaption };
    if (tagIds.length > 0) {
      payload.tag = tagIds;
    }

    setIsUpdatingEdit(true);
    dispatch(updateContentAction(contentId, payload, (res) => {
      setIsUpdatingEdit(false);
      if (res?.success) {
        setIsEditModalOpen(false);
        onUpdated?.('caption', contentId, { caption: editCaption, tag_users: selectedTaggedUsers });
      } else {
        setIsEditModalOpen(false);
        setTimeout(() => {
          showError('Error', res?.message || 'Failed to update caption');
        }, 100);
      }
    }));
  }, [dispatch, editCaption, onUpdated, post?._id, post?.id, selectedTaggedUsers]);

  // Save new price
  const handlePriceConfirm = useCallback(() => {
    const contentId = post?._id || post?.id;
    if (!contentId) return;

    const num = parseFloat(priceInput);
    if (!priceInput || isNaN(num)) {
      setPriceError('Price is required');
      return;
    }
    if (num <= 0) {
      setPriceError('Price must be greater than 0');
      return;
    }
    if (num < CONTENT_PRICING.MIN_PRICE) {
      setPriceError(`Price must be at least $${CONTENT_PRICING.MIN_PRICE}`);
      return;
    }
    if (num > CONTENT_PRICING.MAX_PRICE) {
      setPriceError(`Price cannot exceed $${CONTENT_PRICING.MAX_PRICE}`);
      return;
    }

    const newPrice = Number(parseFloat(num).toFixed(2));
    setIsUpdatingPrice(true);

    // If we're in a lock flow, send both lock and price together
    const payload = isLockingWithPrice ? { lock: true, price: newPrice } : { price: newPrice };

    dispatch(updateContentAction(contentId, payload, (res) => {
      setIsUpdatingPrice(false);
      setIsPriceModalOpen(false);
      if (res?.success) {
        if (isLockingWithPrice) {
          setIsLockingWithPrice(false);
          onUpdated?.('lock', contentId, newPrice);
        } else {
          onUpdated?.('price', contentId, newPrice);
        }
      } else {
        setTimeout(() => {
          showError('Error', res?.message || 'Failed to update price');
        }, 100);
      }
    }));
  }, [dispatch, isLockingWithPrice, onUpdated, post?._id, post?.id, priceInput]);

  // Pin content
  const onPin = useCallback((post) => {
    const contentId = post?._id || post?.id;
    if (!contentId) return;
    dispatch(updateContentAction(contentId, { pin: true }, (res) => {
      if (res?.success) {
        onUpdated?.('pin', contentId);
      }
    }));
  }, [dispatch, onUpdated, post?._id, post?.id]);

  // Unpin content
  const onUnpin = useCallback((post) => {
    const contentId = post?._id || post?.id;
    if (!contentId) return;
    dispatch(updateContentAction(contentId, { pin: false }, (res) => {
      if (res?.success) {
        onUpdated?.('unpin', contentId);
      }
    }));
  }, [dispatch, onUpdated, post?._id, post?.id]);

  // Delete content with confirmation
  const onDelete = useCallback(async (post) => {
    const contentId = post?._id || post?.id;
    if (!contentId || isDeleting) return;

    const confirmed = await confirmDangerousAction(
      'Delete content?',
      'Are you sure you want to delete this content? This action cannot be undone.'
    );
    if (!confirmed) return;

    setIsDeleting(true);
    dispatch(deleteContentAction(contentId, (res) => {
      setIsDeleting(false);
      if (res?.success) {
        onDeleted?.(contentId);
      }
    }));
  }, [dispatch, onDeleted, post?._id, post?.id, isDeleting]);

  // Handle menu actions
  const handleMenuAction = useCallback((action, e) => {
    e.preventDefault();
    e.stopPropagation();
    closeMenu();

    const actionMap = {
      unlock: () => onUnlock?.(post),
      lock: () => onLock?.(post),
      edit: () => onEdit?.(post),
      price: () => onPriceChange?.(post),
      delete: () => onDelete?.(post),
      pin: () => onPin?.(post),
      unpin: () => onUnpin?.(post),
    };

    actionMap[action]?.();
  }, [closeMenu, onUnlock, onLock, onEdit, onPriceChange, onDelete, onPin, onUnpin, post]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (!event.target.closest(MENU_CONTAINER_SELECTOR)) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeMenu]);

  const modalRoot = (typeof document !== 'undefined' && document.body) || null;

  return (
    <div
      className={`creator-feed-menu-container ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className="creator-feed-menu-trigger"
        type="button"
        onClick={toggleMenu}
        aria-label="Open menu"
        aria-expanded={isOpen}
      >
        <i className={`fa-solid ${iconClass}`} />
      </button>

      <div
        className={`creator-feed-dropdown ${isOpen ? 'show' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="creator-feed-dropdown-item"
          type="button"
          onClick={(e) => handleMenuAction(post.lock_content ? 'unlock' : 'lock', e)}
        >
          <i className={`fa-solid ${getUnlockIcon()}`} />
          <span>{getUnlockText()}</span>
        </button>

        {isShowPin && !isPinAlreadyExists && (
          <button
            className="creator-feed-dropdown-item"
            type="button"
            onClick={(e) => handleMenuAction(post?.pin ? 'unpin' : 'pin', e)}
          >
            <i className={`fa-solid ${post?.pin ? 'fa-thumbtack' : 'fa-thumbtack'}`} />
            <span>{post?.pin ? 'Unpin' : 'Pin'}</span>
          </button>
        )}
        {isShowPin && isPinAlreadyExists && post?.pin && (
          <button
            className="creator-feed-dropdown-item"
            type="button"
            onClick={(e) => handleMenuAction('unpin', e)}
          >
            <i className="fa-solid fa-thumbtack" />
            <span>Unpin</span>
          </button>
        )}

        <button
          className="creator-feed-dropdown-item"
          type="button"
          onClick={(e) => handleMenuAction('edit', e)}
        >
          <i className="fa-solid fa-pen-to-square" />
          <span>Edit Caption</span>
        </button>

        {canChangePrice() && (
          <button
            className="creator-feed-dropdown-item"
            type="button"
            onClick={(e) => handleMenuAction('price', e)}
          >
            <i className="fa-solid fa-dollar-sign" />
            <span>Change Price</span>
          </button>
        )}

        <button
          className={`creator-feed-dropdown-item delete ${isDeleting ? 'loading' : ''}`}
          type="button"
          onClick={(e) => handleMenuAction('delete', e)}
          disabled={isDeleting}
          aria-busy={isDeleting}
        >
          {isDeleting ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" />
              <span>Deleting...</span>
            </>
          ) : (
            <>
              <i className="fa-solid fa-trash" />
              <span>Delete</span>
            </>
          )}
        </button>
      </div>

      {/* Edit price modal */}
      {isPriceModalOpen &&
        modalRoot &&
        createPortal(
          <div className="jh-modal-overlay cm_bg_blur" role="dialog" aria-modal="true">
            <div className="jh-modal">
              <div className="jh-modal-header" style={{ justifyContent: 'center' }}>
                <h4 style={{ textAlign: 'center', flex: 1 }}>Set Content Price</h4>
                <button className="jh-modal-close" onClick={closePriceModal} aria-label="Close">
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
              <div className="jh-modal-body">
                <label htmlFor="content-price" className="jh-modal-label">
                  {`Enter a price between $${CONTENT_PRICING.MIN_PRICE} and $${CONTENT_PRICING.MAX_PRICE}`}
                </label>
                <input
                  id="content-price"
                  type="number"
                  className="jh-modal-input"
                  step="0.01"
                  min={CONTENT_PRICING.MIN_PRICE}
                  max={CONTENT_PRICING.MAX_PRICE}
                  inputMode="decimal"
                  value={priceInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Allow empty, digits, or digits with up to 2 decimals
                    if (val === '' || /^\d*(\.\d{0,2})?$/.test(val)) {
                      setPriceInput(val);
                      setPriceError('');
                    }
                  }}
                  onKeyDown={(e) => {
                    const allowedControlKeys = [
                      'Backspace',
                      'Delete',
                      'Tab',
                      'ArrowLeft',
                      'ArrowRight',
                      'Home',
                      'End',
                      'Enter',
                    ];
                    if (allowedControlKeys.includes(e.key)) return;
                    // Block invalid scientific/plus/minus inputs often allowed in number fields
                    if (['e', 'E', '+', '-'].includes(e.key)) {
                      e.preventDefault();
                      return;
                    }
                    // Allow digits
                    if (e.key >= '0' && e.key <= '9') return;
                    // Allow one decimal point, but not more than one
                    if (e.key === '.') {
                      const { selectionStart, selectionEnd } = e.currentTarget;
                      const current = e.currentTarget.value || '';
                      const next =
                        current.slice(0, selectionStart) + '.' + current.slice(selectionEnd);
                      // Prevent if a dot already exists or would cause more than 2 decimals
                      if (current.includes('.') || !/^\d*(\.\d{0,2})?$/.test(next)) {
                        e.preventDefault();
                      }
                      return;
                    }
                    // Block everything else (letters, spaces, etc.)
                    e.preventDefault();
                  }}
                  onPaste={(e) => {
                    const pasted = (e.clipboardData || window.clipboardData).getData('text');
                    const { selectionStart, selectionEnd } = e.currentTarget;
                    const current = e.currentTarget.value || '';
                    const next = current.slice(0, selectionStart) + pasted + current.slice(selectionEnd);
                    if (!(next === '' || /^\d*(\.\d{0,2})?$/.test(next))) {
                      e.preventDefault();
                    }
                  }}
                />
                <p style={{ color: 'gray', fontSize: '0.85rem', margin: '8px 0 0 0' }}>
                  All transactions are processed in USD.
                </p>
                {priceError ? <div className="jh-modal-error">{priceError}</div> : null}
              </div>
              <div className="jh-modal-footer">
                <button className="unified-edit-cancel-btn" onClick={closePriceModal} disabled={isUpdatingPrice}>
                  Cancel
                </button>
                <button className="unified-edit-save-btn" onClick={handlePriceConfirm} disabled={isUpdatingPrice}>
                  {isUpdatingPrice ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin" />
                      <span>Updating...</span>
                    </>
                  ) : (
                    'Update Price'
                  )}
                </button>
              </div>
            </div>
          </div>,
          modalRoot
        )}

      {/* Edit caption modal */}
      {isEditModalOpen &&
        modalRoot &&
        createPortal(
          <div className="jh-modal-overlay cm_bg_blur" role="dialog" aria-modal="true">
            <div className="jh-modal">
              <div className="jh-modal-header">
                <h4>Edit Caption</h4>
                <button className="jh-modal-close" onClick={closeEditModal} aria-label="Close">
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
              <div className="jh-modal-body">
                <label htmlFor="content-caption" className="jh-modal-label">Caption</label>
                <textarea
                  id="content-caption"
                  rows={3}
                  className="jh-modal-textarea"
                  value={editCaption}
                  onChange={(e) => setEditCaption(e.target.value)}
                  placeholder="Write a caption..."
                />

                <div className="jh-modal-label mt-2">Tagged users</div>
                <div className="caption-tag-section">
                  {Array.isArray(selectedTaggedUsers) && selectedTaggedUsers.length > 0 ? (
                    <>
                      {selectedTaggedUsers.map((user) => (
                        <span key={user.username || user._id || user.id} className="tagged-user-chip">
                          @{user.username}
                          <button
                            className="tagged-user-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveTaggedUser(user.username);
                            }}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        </span>
                      ))}
                      <button className="caption-add-tag-button" onClick={openTagPopup}>
                        <i className="fa fa-plus" />
                      </button>
                    </>
                  ) : (
                    <button className="caption-tag-button" onClick={openTagPopup}>
                      Tag
                    </button>
                  )}
                </div>
              </div>
              <div className="jh-modal-footer">
                <button className="unified-edit-cancel-btn" onClick={closeEditModal} disabled={isUpdatingEdit}>
                  Cancel
                </button>
                <button className="unified-edit-save-btn" onClick={handleEditSave} disabled={isUpdatingEdit}>
                  {isUpdatingEdit ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>,
          modalRoot
        )}

      {/* UserTag Modal - Render via portal to ensure it appears above edit modal */}
      {isTagPopupOpen &&
        modalRoot &&
        createPortal(
          <UserTag
            isOpen={isTagPopupOpen}
            initialSelectedUsers={selectedTaggedUsers}
            onConfirm={handleTagConfirm}
            onClose={closeTagPopup}
          />,
          modalRoot
        )}
    </div>
  );
};

// PropTypes for type checking
MediaGridDropdown.propTypes = {
  className: PropTypes.string,
  iconClass: PropTypes.string,
  post: PropTypes.object.isRequired,
  userRole: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  isPinAlreadyExists: PropTypes.bool,
  isShowPin: PropTypes.bool,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  onDeleted: PropTypes.func,
  onUpdated: PropTypes.func,
};

export default MediaGridDropdown;

